/**
 * Operações em Lote (migrada do Streamlit; equivalência das aplicações BTG em
 * backend/tests_paginas/test_operacoes.py; XP e resgates em backend/tests/api/test_operacoes_api.py).
 *
 * Consulta, conta a conta e em segundo plano no servidor, o caixa disponível D0 e a posição
 * no fundo de liquidez pelas APIs do BTG e da XP (somente leitura). Caixa positivo vira
 * aplicação (zeragem) e caixa negativo vira resgate para cobri-lo; cada direção tem seu
 * Excel para envio manual ao custodiante. Na URL: custodiante, fundo, margem do resgate e a
 * rodada de consulta (cada clique em "Atualizar" é uma consulta nova; o link reabre a mesma
 * consulta enquanto ela estiver guardada, 1 h). Mudar a margem não consulta de novo.
 */
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Text,
  TextInput,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, apiPost, montarUrl, type Envelope } from '../../api/cliente';
import { Cronometro } from '../../components/Cronometro';
import { BarraFiltros, CampoOpcoes, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Kpi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';
import { INTERVALO_CONSULTA } from '../quant/FactorInvesting';

const CAMINHO = '/carteiras-administradas/operacoes-em-lote';
const OUTRO = 'outro';

type Custodiante = 'BTG CTVM' | 'XPCV';
interface Fundo {
  cnpj: string;
  nome: string;
}
interface Opcoes {
  fundos: Record<Custodiante, Fundo[]>;
  n_contas_btg: number;
  btg_configurado: boolean;
  n_contas_xp: number;
  xp_configurado: boolean;
  resgate_minimo: number;
}
interface Estado {
  status: 'rodando' | 'pronto' | 'erro';
  iniciado_em?: string;
  mensagem?: string;
  n_contas: number;
  aviso_erros?: string;
  resultado?: {
    consultado_em: string;
    tabela: TabelaApi;
    n_aplicacoes: number;
    total_aplicacoes: string;
    n_resgates: number;
    total_resgates: string;
    n_alertas: number;
    cnpj: string;
  };
}

const CUSTODIANTES: Record<
  Custodiante,
  { rotulo: string; consultando: string; base: string; semApi: string }
> = {
  'BTG CTVM': {
    rotulo: 'BTG',
    consultando: 'Consultando o BTG',
    base: 'O BTG lê o valor do resgate como líquido: a cobertura é conferida contra a posição líquida. Aplicação também exige o código CGE do fundo.',
    semApi: 'A API do BTG não está configurada neste servidor.',
  },
  XPCV: {
    rotulo: 'XP',
    consultando: 'Consultando a XP',
    base: 'A XP lê o valor do resgate como bruto: a cobertura é conferida contra a posição bruta.',
    semApi: 'A API da XP não está configurada neste servidor.',
  },
};

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

const digitos = (s: string) => s.replace(/\D/g, '');

export function OperacoesLote() {
  const [f, definir] = useFiltrosUrl({
    custodiante: 'BTG CTVM',
    fundo: '',
    cnpj: '',
    margem: 0,
    rodada: '',
  });
  const custodiante: Custodiante = f.custodiante === 'XPCV' ? 'XPCV' : 'BTG CTVM';
  const c = CUSTODIANTES[custodiante];
  const opcoes = useQuery({
    queryKey: ['carteiras.operacoes.opcoes'],
    queryFn: ({ signal }) => apiGet<Envelope<Opcoes>>(CAMINHO, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const o = opcoes.data?.data;
  const fundos = o?.fundos[custodiante] ?? [];
  const fundo =
    f.fundo === OUTRO || fundos.some((x) => x.cnpj === f.fundo) ? f.fundo : fundos[0]?.cnpj || '';
  const cnpj = fundo === OUTRO ? f.cnpj : fundo;
  const cnpjValido = digitos(cnpj).length === 14;
  const nContas = custodiante === 'XPCV' ? o?.n_contas_xp : o?.n_contas_btg;
  const configurado = custodiante === 'XPCV' ? o?.xp_configurado : o?.btg_configurado;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Operações em Lote"
        descricao="Aplicações e resgates em massa no fundo de liquidez: zera o caixa positivo e cobre o caixa negativo de cada conta BTG CTVM ou XPCV, com o Excel de cada direção para envio manual ao custodiante."
        fonte="Fibery (contas sob gestão) · API BTG Pactual Wealth Services e XP Data Access (saldos, somente leitura)"
      />
      <BarraFiltros carregando={opcoes.isFetching}>
        <GrupoFiltros titulo="Custodiante e fundo">
          <CampoOpcoes
            rotulo="Custodiante"
            valor={custodiante}
            aoMudar={(v) => definir({ custodiante: v, fundo: '', cnpj: '', rodada: '' })}
            opcoes={[
              { valor: 'BTG CTVM', rotulo: 'BTG CTVM' },
              { valor: 'XPCV', rotulo: 'XPCV' },
            ]}
          />
          <Select
            label="Fundo"
            data={[
              ...fundos.map((x) => ({ value: x.cnpj, label: x.nome })),
              { value: OUTRO, label: 'Outro (informar CNPJ)' },
            ]}
            value={fundo || null}
            onChange={(v) => v && definir({ fundo: v, rodada: '' })}
            allowDeselect={false}
            w={LARGURA.selecao + 60}
          />
          {fundo === OUTRO && (
            <TextInput
              label="CNPJ do fundo"
              placeholder="XX.XXX.XXX/XXXX-XX"
              value={f.cnpj}
              onChange={(e) => definir({ cnpj: e.currentTarget.value, rodada: '' })}
              error={f.cnpj && !cnpjValido ? 'CNPJ com 14 dígitos' : undefined}
              w={LARGURA.selecao - 40}
            />
          )}
        </GrupoFiltros>
        <GrupoFiltros titulo="Resgate">
          <NumberInput
            label="Margem sobre o negativo (%)"
            min={0}
            max={100}
            decimalScale={2}
            decimalSeparator=","
            value={f.margem}
            onChange={(v) => typeof v === 'number' && v >= 0 && v <= 100 && definir({ margem: v })}
            w={LARGURA.numero + 60}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Saldos">
          <Button
            mt={25}
            color="dourado"
            disabled={!cnpjValido || !nContas || !configurado}
            onClick={() => definir({ rodada: String(Date.now()) })}
          >
            Atualizar {custodiante}
          </Button>
        </GrupoFiltros>
      </BarraFiltros>

      {opcoes.isError && (
        <Alert color="red">{mensagem(opcoes.error, 'Não foi possível carregar as contas.')}</Alert>
      )}
      {o && !configurado && <Alert color="yellow">{c.semApi}</Alert>}
      {o && (
        <Text size="sm" c="dimmed">
          {nContas} contas {custodiante} sob gestão. Caixa disponível D0 acima de R$ 100 vira
          aplicação; caixa negativo vira resgate de negativo × (1 + margem), no mínimo{' '}
          {o.resgate_minimo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. Se a
          posição no fundo não cobre, resgate total e alerta para resolver a conta na mão.
        </Text>
      )}
      {!f.rodada && (
        <Alert color="blue">
          Clique em <b>Atualizar {custodiante}</b> para consultar os saldos do fundo selecionado.
        </Alert>
      )}
      <Saldos
        custodiante={custodiante}
        cnpj={cnpj}
        rodada={f.rodada}
        margem={f.margem}
        cnpjValido={cnpjValido}
      />
    </Pagina>
  );
}

function Saldos({
  custodiante,
  cnpj,
  rodada,
  margem,
  cnpjValido,
}: {
  custodiante: Custodiante;
  cnpj: string;
  rodada: string;
  margem: number;
  cnpjValido: boolean;
}) {
  const c = CUSTODIANTES[custodiante];
  const corpo = { cnpj, custodiante, rodada, margem };
  const consulta = useQuery({
    queryKey: ['carteiras.operacoes.saldos', corpo],
    queryFn: ({ signal }) => apiPost<Envelope<Estado>>(`${CAMINHO}/saldos`, corpo, signal),
    enabled: !!rodada && cnpjValido,
    placeholderData: (anterior) => anterior,
    refetchIntervalInBackground: true,
    refetchInterval: (q) => (q.state.data?.data.status === 'rodando' ? INTERVALO_CONSULTA : false),
  });
  const estado = consulta.data?.data;
  const r = estado?.status === 'pronto' ? estado.resultado : undefined;
  if (!rodada) return null;

  const exportar = (operacao: 'aplicacao' | 'resgate', rotulo: string, n: number) =>
    n > 0 && (
      <Button
        size="xs"
        color="dourado"
        component="a"
        href={montarUrl(`${CAMINHO}/zeragem`, { ...corpo, operacao })}
      >
        {rotulo} ({c.rotulo})
      </Button>
    );

  return (
    <>
      {consulta.isError && (
        <Alert color="red">
          {mensagem(consulta.error, `Não foi possível consultar os saldos ${c.rotulo}.`)}
        </Alert>
      )}
      {consulta.isPending && cnpjValido && <Loader />}
      {estado?.status === 'rodando' && (
        <Alert color="blue" icon={<Loader size="sm" />} title={c.consultando}>
          {estado.n_contas} contas, uma a uma. Iniciado em {formatarDataHora(estado.iniciado_em)}{' '}
          (há <Cronometro desde={estado.iniciado_em ?? ''} />
          ); o resultado aparece aqui sozinho.
        </Alert>
      )}
      {estado?.status === 'erro' && (
        <Alert color="red" title={`A consulta ${c.rotulo} falhou`}>
          {estado.mensagem}
        </Alert>
      )}
      {estado?.aviso_erros && <Alert color="yellow">{estado.aviso_erros}</Alert>}
      {r && (
        <Secao
          titulo={`Saldos ${custodiante}`}
          legenda={`Consultado em ${formatarDataHora(r.consultado_em)}. Azul: aplicação; laranja: resgate; vermelho: alerta. ${c.base}`}
          acoes={
            <Group gap="xs">
              {exportar('aplicacao', 'Excel de aplicações', r.n_aplicacoes)}
              {exportar('resgate', 'Excel de resgates', r.n_resgates)}
            </Group>
          }
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
            <Kpi kpi={{ rotulo: `Aplicações (${r.n_aplicacoes})`, valor: r.total_aplicacoes }} />
            <Kpi kpi={{ rotulo: `Resgates (${r.n_resgates})`, valor: r.total_resgates }} />
            <Kpi kpi={{ rotulo: 'Contas com alerta', valor: String(r.n_alertas) }} />
          </SimpleGrid>
          <TabelaDados tabela={r.tabela} alturaAutomatica />
          {r.n_aplicacoes === 0 && r.n_resgates === 0 && (
            <Group mt="sm">
              <Alert color="blue">
                Nenhuma conta {custodiante} com aplicação ou resgate no momento.
              </Alert>
            </Group>
          )}
        </Secao>
      )}
    </>
  );
}
